import { useCallback, useRef, useState } from 'react';
const CITATION_TYPES: CitationType[] = ['web', 'kb', 'files'];

const useCitation = () => {
  const [citations, setCitations] = useState<CitationGroup[]>([]);
  const citationsRef = useRef<CitationGroup[]>([]);

  const resolveFlatCitationType = useCallback((raw: any): CitationType => {
    if (raw?.type === 'web' || raw?.type === 'kb' || raw?.type === 'files') {
      return raw.type;
    }
    if (
      raw?.citation_type === 'web' ||
      raw?.citation_type === 'kb' ||
      raw?.citation_type === 'files'
    ) {
      return raw.citation_type;
    }
    if (
      typeof raw?.source === 'string' &&
      raw.source.startsWith('ingestion/internal/content/private/')
    ) {
      return 'files';
    }
    if (typeof raw?.url === 'string' && /^https?:\/\//i.test(raw.url)) {
      return 'web';
    }

    return 'kb';
  }, []);

  const normalizeCitationReference = useCallback(
    (citation: any): CitationReference => ({
      id: citation?.id,
      owner: citation?.owner,
      source: citation?.source,
      title: citation?.title,
      url: citation?.url,
      type: citation?.type ?? citation?.citation_type ?? resolveFlatCitationType(citation),
      cited_text: citation?.cited_text ?? citation?.description,
      favIcon: citation?.favIcon,
    }),
    [resolveFlatCitationType]
  );

  const parseCitationRawBlock = useCallback(
    (rawCitationBlock: any): CitationReference[] => {
      let raw = rawCitationBlock;

      if (typeof rawCitationBlock?.content === 'string') {
        try {
          raw = JSON.parse(rawCitationBlock.content);
        } catch (error) {
          console.error('Error parsing history citation block content:', error);

          raw = rawCitationBlock;
        }
      } else if (rawCitationBlock?.content && typeof rawCitationBlock.content === 'object') {
        raw = rawCitationBlock.content;
      }
      if (Array.isArray(raw?.citations) && raw.citations.length > 0) {
        return raw.citations.map(normalizeCitationReference);
      }

      if (Array.isArray(raw?.references) && raw.references.length > 0) {
        return raw.references.map(normalizeCitationReference);
      }

      if (raw?.title) {
        return [normalizeCitationReference(raw)];
      }

      return [];
    },
    [normalizeCitationReference]
  );

  const groupReferencesByType = useCallback((references: CitationReference[]) => {
    const grouped: Record<CitationType, CitationReference[]> = {
      web: [],
      kb: [],
      files: [],
    };

    references.forEach((reference) => {
      const type = reference.type;

      if (CITATION_TYPES.includes(type)) {
        grouped[type].push(reference);
      }
    });

    return grouped;
  }, []);

  const createCitationGroups = useCallback(
    (references: CitationReference[], startingIndex: number): CitationGroup[] => {
      const grouped = groupReferencesByType(references);

      const groups: CitationGroup[] = [];
      let index = startingIndex;

      CITATION_TYPES.forEach((citationType) => {
        const typeReferences = grouped[citationType];

        if (typeReferences.length === 0) {
          return;
        }

        groups.push({
          index,
          citationType,
          references: typeReferences,
        });

        index += 1;
      });

      return groups;
    },
    [groupReferencesByType]
  );

  const registerCitationEvent = useCallback(
    (citationData: any): string => {
      try {
        const rawRefs = citationData?.references;
        if (!Array.isArray(rawRefs) || rawRefs.length === 0) return '';

        const refs = rawRefs.map(normalizeCitationReference);
        const startingIndex = citationsRef.current.length;
        const citationGroups = createCitationGroups(refs, startingIndex);
        if (citationGroups.length === 0) return '';

        citationsRef.current.push(...citationGroups);
        setCitations([...citationsRef.current]);

        return citationGroups
          .map((group) => `<citation-badge data-idx="${group.index}"></citation-badge>`)
          .join('');
      } catch (error) {
        console.error('Error parsing citation event:', error);
        return '';
      }
    },
    [createCitationGroups, normalizeCitationReference]
  );

  const extractCitationsFromRawBlocks = useCallback(
    (
      rawBlocks: any[]
    ): {
      blocks: any[];
      citations: CitationGroup[];
    } => {
      if (!Array.isArray(rawBlocks) || rawBlocks.length === 0) {
        return {
          blocks: rawBlocks || [],
          citations: [],
        };
      }

      const blocks = [...rawBlocks];
      const extractedCitations: CitationGroup[] = [];

      for (let blockIndex = 0; blockIndex < blocks.length; blockIndex++) {
        const currentBlock = blocks[blockIndex];

        if (!currentBlock || currentBlock.type !== 'citation') {
          continue;
        }

        const citationRawBlocks = [currentBlock];
        let nextIndex = blockIndex + 1;

        while (nextIndex < blocks.length) {
          const nextBlock = blocks[nextIndex];

          if (nextBlock?.type === 'text') {
            break;
          }
          if (nextBlock?.type === 'citation') {
            citationRawBlocks.push(nextBlock);
            blocks.splice(nextIndex, 1);
            continue;
          }
          nextIndex++;
        }

        const refs: CitationReference[] = [];

        citationRawBlocks.forEach((rawCitationBlock) => {
          refs.push(...parseCitationRawBlock(rawCitationBlock));
        });

        if (refs.length === 0) {
          blocks.splice(blockIndex, 1);
          blockIndex--;
          continue;
        }

        const citationGroups = createCitationGroups(refs, extractedCitations.length);
        extractedCitations.push(...citationGroups);

        const markers = citationGroups
          .map((group) => `<citation-badge data-idx="${group.index}"></citation-badge>`)
          .join('');

        const previousBlock = blocks[blockIndex - 1];
        const followingBlock = blocks[nextIndex];

        if (markers && previousBlock?.type === 'text') {
          previousBlock.content += markers;
          blocks.splice(blockIndex, 1);
          blockIndex--;
        } else if (markers && followingBlock?.type === 'text') {
          followingBlock.content = markers + followingBlock.content;
          blocks.splice(blockIndex, 1);
          blockIndex--;
        } else if (markers) {
          blocks[blockIndex] = {
            type: 'text',
            content: markers,
          };
        } else {
          blocks.splice(blockIndex, 1);
          blockIndex--;
        }
      }
      return {
        blocks,
        citations: extractedCitations,
      };
    },
    [createCitationGroups, parseCitationRawBlock]
  );

  const resetCitations = useCallback(() => {
    citationsRef.current = [];
    setCitations([]);
  }, []);

  const getCurrentCitations = useCallback(() => {
    return citationsRef.current;
  }, []);

  return {
    citations,
    registerCitationEvent,
    extractCitationsFromRawBlocks,
    parseCitationRawBlock,
    resolveFlatCitationType,
    resetCitations,
    getCurrentCitations,
  };
};

export default useCitation;
